import React, { useState } from "react";
import { Mail, Eye, Code, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const PLACEHOLDERS = [
  { key: "{{from_name}}", label: "Visitor Name" },
  { key: "{{from_email}}", label: "Visitor Email" },
  { key: "{{company}}", label: "Company" },
  { key: "{{phone}}", label: "Phone" },
  { key: "{{service}}", label: "Service" },
  { key: "{{message}}", label: "Message" },
  { key: "{{submission_date}}", label: "Date" },
  { key: "{{reference_id}}", label: "Reference ID" },
];

export default function VisitorEmailSettings({ settings, onSave }) {
  const [cfg, setCfg] = useState({ ...settings });

  const update = (key, value) => setCfg((prev) => ({ ...prev, [key]: value }));
  const handleSave = () => onSave(cfg);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            <Mail className="w-5 h-5 text-cyan-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Visitor Acknowledgment Email</h2>
            <p className="text-sm text-slate-500">Auto-reply sent to the visitor after form submission</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-5">
          <div>
            <Label className="text-slate-700 font-medium">Enable Visitor Email</Label>
            <p className="text-xs text-slate-500 mt-1">Turns visitor confirmation email on/off</p>
          </div>
          <Switch data-testid="visitor-enabled-toggle" checked={cfg.visitorEmailEnabled !== false} onCheckedChange={(v) => update("visitorEmailEnabled", v)} />
        </div>

        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Reply-To Email *</Label>
              <Input data-testid="visitor-reply-to" value={cfg.visitorReplyTo || ""} onChange={(e) => update("visitorReplyTo", e.target.value)} placeholder="info@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">CC Email</Label>
              <Input data-testid="visitor-cc" value={cfg.visitorCc || ""} onChange={(e) => update("visitorCc", e.target.value)} placeholder="sales@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-700">BCC Email</Label>
            <Input data-testid="visitor-bcc" value={cfg.visitorBcc || ""} onChange={(e) => update("visitorBcc", e.target.value)} placeholder="admin@yourdomain.com" className="bg-slate-50 border-slate-200" />
          </div>
        </div>
      </div>

      {/* Branding */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-5">Branding & Signature</h3>
        <p className="text-sm text-slate-500 mb-4">These values are passed to your EmailJS template as placeholders.</p>
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Company Name</Label>
              <Input data-testid="visitor-company-name" value={cfg.companyName || ""} onChange={(e) => update("companyName", e.target.value)} placeholder="ElitePlus Consulting" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Signature Name</Label>
              <Input data-testid="visitor-sig-name" value={cfg.signatureName || ""} onChange={(e) => update("signatureName", e.target.value)} placeholder="ElitePlus Consulting Team" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Signature Email</Label>
              <Input data-testid="visitor-sig-email" value={cfg.signatureEmail || ""} onChange={(e) => update("signatureEmail", e.target.value)} placeholder="info@yourdomain.com" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Signature Phone</Label>
              <Input data-testid="visitor-sig-phone" value={cfg.signaturePhone || ""} onChange={(e) => update("signaturePhone", e.target.value)} placeholder="+971 7 5011591" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-slate-700">Website URL</Label>
              <Input data-testid="visitor-website-url" value={cfg.websiteUrl || ""} onChange={(e) => update("websiteUrl", e.target.value)} placeholder="https://eliteplus.io" className="bg-slate-50 border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Logo URL</Label>
              <Input data-testid="visitor-logo-url" value={cfg.logoUrl || ""} onChange={(e) => update("logoUrl", e.target.value)} placeholder="https://yourdomain.com/logo.png" className="bg-slate-50 border-slate-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Template Placeholders Reference */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-slate-800 mb-3">Template Placeholders</h3>
        <p className="text-sm text-slate-500 mb-4">Use these placeholders in your EmailJS visitor template:</p>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDERS.map((p) => (
            <span key={p.key} className="px-3 py-1.5 text-xs bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-md font-mono">
              {p.key} <span className="text-cyan-400 font-sans">— {p.label}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button data-testid="save-visitor-btn" onClick={handleSave} className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-8">
          Save Visitor Email Settings
        </Button>
      </div>
    </div>
  );
}
